import { Cell, TableData } from 'utils/data'

export type RgdLocation = {
    location_id: string
    pickup?: number | string | null
    grocery_pickup?: number | string | null
    drive_up?: number | string | null
    drive_up_returns?: number | string | null
    drive_up_starbucks?: number | string | null
    parking_lot_delivery_pause?: number | string | null
    sfs?: number | string | null
    ndd?: number | string | null
    sdd?: number | string | null
}

export type RgdCapabilitiesData = {
    current_location?: RgdLocation
    parent_locations?: RgdLocation[]
    child_locations?: RgdLocation[]
}

export const formatCapabilityValue = (
    value: string | number | null | undefined,
) => (value == null ? '—' : String(value))

export const buildOrderedLocations = (
    data?: RgdCapabilitiesData,
): RgdLocation[] => {
    const parentLocations = data?.parent_locations ?? []
    const childLocations = data?.child_locations ?? []
    const currentLocation = data?.current_location

    const orderedLocations: RgdLocation[] = []
    const seenLocations = new Set<string>()

    parentLocations.forEach((location) => {
        if (!seenLocations.has(location.location_id)) {
            orderedLocations.push(location)
            seenLocations.add(location.location_id)
        }
    })

    if (currentLocation && !seenLocations.has(currentLocation.location_id)) {
        orderedLocations.push(currentLocation)
        seenLocations.add(currentLocation.location_id)
    }

    childLocations.forEach((location) => {
        if (!seenLocations.has(location.location_id)) {
            orderedLocations.push(location)
            seenLocations.add(location.location_id)
        }
    })

    return orderedLocations
}

export const buildRgdTableData = (
    rows: RgdLocation[],
    headers: string[],
): TableData => ({
    headings: headers.map((header): Cell => ({ value: header })),
    rows: rows.map((row) => [
        { value: row.location_id },
        { value: formatCapabilityValue(row.pickup) },
        { value: formatCapabilityValue(row.grocery_pickup) },
        { value: formatCapabilityValue(row.drive_up) },
        { value: formatCapabilityValue(row.drive_up_returns) },
        { value: formatCapabilityValue(row.drive_up_starbucks) },
        { value: formatCapabilityValue(row.parking_lot_delivery_pause) },
        { value: formatCapabilityValue(row.sfs) },
        { value: formatCapabilityValue(row.ndd) },
        { value: formatCapabilityValue(row.sdd) },
    ]),
})

export const sortChildRows = (
    tableData: TableData,
    childLocationIds: Set<string>,
    columnIndex: number,
    sortDirection: 'asc' | 'dsc',
): TableData => {
    const parentAndCurrentRows = tableData.rows.filter(
        (row) => !childLocationIds.has(String(row[0].value)),
    )
    const childRows = tableData.rows.filter((row) =>
        childLocationIds.has(String(row[0].value)),
    )

    const sortedChildRows = [...childRows].sort((a, b) => {
        const aValue = String(a[columnIndex]?.value ?? '')
        const bValue = String(b[columnIndex]?.value ?? '')

        if (aValue === '—' && bValue === '—') return 0
        if (aValue === '—') return 1
        if (bValue === '—') return -1

        const aNum = Number(aValue)
        const bNum = Number(bValue)
        const bothNumeric = !Number.isNaN(aNum) && !Number.isNaN(bNum)

        const result = bothNumeric ? aNum - bNum : aValue.localeCompare(bValue)

        return sortDirection === 'asc' ? result : -result
    })

    return {
        ...tableData,
        rows: [...parentAndCurrentRows, ...sortedChildRows],
    }
}
